import { useState } from "react";
import { X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCTS_ES = [
  { value: "botella-500ml", label: "Botella 500 ml" },
  { value: "estuche-regalo", label: "Estuche regalo (2 × 500 ml)" },
  { value: "pack-6", label: "Pack 6 botellas" },
  { value: "pack-9", label: "Pack 9 botellas" },
  { value: "pack-12", label: "Pack 12 botellas" },
];
const PRODUCTS_EN = [
  { value: "botella-500ml", label: "500 ml bottle" },
  { value: "estuche-regalo", label: "Gift box (2 × 500 ml)" },
  { value: "pack-6", label: "6-bottle pack" },
  { value: "pack-9", label: "9-bottle pack" },
  { value: "pack-12", label: "12-bottle pack" },
];

export default function WaitlistForm({ onClose, preselectedProduct = "" }) {
  const { t, lang } = useLang();
  const w = t("waitlist");
  const products = lang === "es" ? PRODUCTS_ES : PRODUCTS_EN;
  const privacyHref = "/legal/privacidad";

  const [email, setEmail] = useState("");
  const [product, setProduct] = useState(preselectedProduct);
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error | duplicate
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!consent) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          product_interest: product || null,
          lang,
          consent: true,
          marketing_consent: marketing,
          privacy_version: "1.0",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(w.error_generic);
        setStatus("error");
        return;
      }
      if (data.status === "already_registered") {
        setStatus("duplicate");
        return;
      }
      setStatus("success");
    } catch {
      setErrorMsg(w.error_generic);
      setStatus("error");
    }
  }

  if (status === "success" || status === "duplicate") {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wl-success-title"
      >
        <div className="relative w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
          <button
            onClick={onClose}
            aria-label={w.close}
            className="absolute right-4 top-4 text-stone-400 hover:text-stone-700 transition-colors"
            data-testid="waitlist-close-btn"
          >
            <X size={20} />
          </button>
          <div className="mb-4 text-4xl">✓</div>
          <h2
            id="wl-success-title"
            className="font-serif text-2xl text-stone-900 mb-3"
            data-testid="waitlist-success-title"
          >
            {status === "duplicate" ? w.already_registered : w.success_title}
          </h2>
          <p className="text-stone-600 text-sm leading-relaxed">
            {w.success_body}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wl-title"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <button
          onClick={onClose}
          aria-label={w.close}
          className="absolute right-4 top-4 text-stone-400 hover:text-stone-700 transition-colors"
          data-testid="waitlist-close-btn"
        >
          <X size={20} />
        </button>

        <h2
          id="wl-title"
          className="font-serif text-2xl text-stone-900 mb-2"
          data-testid="waitlist-modal-title"
        >
          {w.title}
        </h2>
        <p className="text-sm text-stone-500 mb-6 leading-relaxed">{w.subtitle}</p>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Email */}
          <div>
            <label
              htmlFor="wl-email"
              className="block text-xs font-medium text-stone-700 mb-1"
            >
              {w.email_label} <span aria-hidden="true" className="text-red-500">*</span>
            </label>
            <input
              id="wl-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={w.email_placeholder}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-stone-700 focus:outline-none focus:ring-1 focus:ring-stone-700"
              data-testid="waitlist-email-input"
            />
          </div>

          {/* Product interest */}
          <div>
            <label
              htmlFor="wl-product"
              className="block text-xs font-medium text-stone-700 mb-1"
            >
              {w.product_label}
            </label>
            <select
              id="wl-product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm bg-white focus:border-stone-700 focus:outline-none focus:ring-1 focus:ring-stone-700"
              data-testid="waitlist-product-select"
            >
              <option value="">{w.product_placeholder}</option>
              {products.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {/* Consent - required, unchecked */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-stone-400 accent-stone-700"
              data-testid="waitlist-consent-checkbox"
            />
            <span className="text-xs text-stone-700 leading-relaxed">
              {w.consent_label}{" "}
              <span aria-hidden="true" className="text-red-500">*</span>
            </span>
          </label>

          {/* Marketing - optional, unchecked */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-stone-400 accent-stone-700"
              data-testid="waitlist-marketing-checkbox"
            />
            <span className="text-xs text-stone-500 leading-relaxed">
              {w.marketing_label}
            </span>
          </label>

          {/* Privacy link + data note */}
          <div className="space-y-1.5">
            <p className="text-xs text-stone-500 leading-relaxed">
              {w.data_note}
            </p>
            <p className="text-xs text-stone-400 leading-relaxed">
              {w.privacy_text}{" "}
              <a
                href={privacyHref}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-stone-700 transition-colors"
                data-testid="waitlist-privacy-link"
              >
                {w.privacy_link}
              </a>
              {w.privacy_text2 && <span>{w.privacy_text2}</span>}
            </p>
          </div>

          {status === "error" && (
            <p className="text-xs text-red-600" role="alert" data-testid="waitlist-error">
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={!consent || !email || status === "loading"}
            className="w-full rounded-lg bg-stone-900 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="waitlist-submit-btn"
          >
            {status === "loading" ? "…" : w.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
